import {useCallback, useEffect} from 'react';
import {useLazyQuery} from '@apollo/client';
import {useAppDispatch} from '@/Pages/Common/hooks/useAppDispatch';
import {useAppSelector} from '@/Pages/Common/hooks/useAppSelector';
import getBusPartnersCostYearsQuery from '@/Pages/PartnerCosts/graphql/getBusPartnersCostYears';
import {BusPartnerCostYear} from '../store/types';
import {busPartnersCostYearsFetched} from '../store/slice';

interface UseBusPartnersYearsResult {
  busPartnersCostYears: BusPartnerCostYear[] | null;
  refetchBusPartnersCostYears: () => Promise<void>;
}

export const useBusPartnersCostYears = (): UseBusPartnersYearsResult => {
  const busPartnersCostYears = useAppSelector(
    ({partnerCosts}) => partnerCosts.busPartnersCostYears
  );
  const dispatch = useAppDispatch();

  const [fetchBusPartnersCostYears] = useLazyQuery<{
    busPartnersCostYears: BusPartnerCostYear[];
  }>(getBusPartnersCostYearsQuery, {
    errorPolicy: 'all',
    fetchPolicy: 'no-cache'
  });

  const getBusPartnersCostYears = useCallback(
    async (force = false) => {
      if (!force && busPartnersCostYears) return;

      try {
        const {data, error} = await fetchBusPartnersCostYears();

        if (error) {
          const errorMessage =
            error.message ?? 'Failed to fetch bus partners cost years.';
          console.error(errorMessage);
          return;
        }

        dispatch(busPartnersCostYearsFetched(data?.busPartnersCostYears ?? []));
      } catch (err) {
        console.error(
          `Unexpected error while fetching bus partners cost years: ${err}`
        );
      }
    },
    [busPartnersCostYears, dispatch, fetchBusPartnersCostYears]
  );

  useEffect(() => {
    getBusPartnersCostYears();
  }, []); // eslint-disable-line

  const refetchBusPartnersCostYears = useCallback(
    () => getBusPartnersCostYears(true),
    [getBusPartnersCostYears]
  );

  return {busPartnersCostYears, refetchBusPartnersCostYears};
};
